import { widgetFilterSchema } from '../../common/schema'
import { okOrThrow } from '../../common/http'
import { homerunnerApiBaseUrl } from '../../hr'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const spacesParamsSchema = widgetFilterSchema.extend({
  feed_id: z.number().int().describe('Filter by feed ID'),
  platform: z.string().nullish().describe('Filter by platform UUID'),
  property: z.string().describe('Filter by property slug'),
}).describe('Data filter options for property spaces')

export type SpacesFilter = z.infer<typeof spacesParamsSchema>

export type SpaceBed = {
  name: string
  type: string
  count: number
}

export type PropertySpace = {
  id: number
  name: string | null
  number: number | null
  type: string | null
  privacy: string | null
  has_private_bathroom: boolean
  beds: SpaceBed[]
  total_bed_count: number
}

/**
 * PMS-default placeholder spaces (#998): sandbox/imported properties carry
 * spaces like "Default bedroom" with no bed data — internal scaffolding, not
 * guest content. A space is suppressed only when it is BOTH contentless (no
 * beds at all) AND default/unnamed; a real named space with no beds still
 * renders, and a "Default bedroom" WITH beds still renders.
 */
export function isPlaceholderSpace(space: PropertySpace): boolean {
  const contentless = space.beds.length === 0 && !(space.total_bed_count > 0)
  const trimmedName = (space.name ?? '').trim()
  const defaultNamed = trimmedName === '' || /^default\b/i.test(trimmedName)
  return contentless && defaultNamed
}

export async function fetchSpaces(filter: SpacesFilter): Promise<PropertySpace[]> {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-spaces`)
  Object.entries(filter).forEach(([key, value]) => {
    if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
  })

  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch property spaces')
  const data = await res.json()

  return (data?.result?.spaces ?? []) as PropertySpace[]
}

export function useFetchSpaces(filter: SpacesFilter) {
  return useQuery({
    queryKey: ['spaces', filter],
    queryFn: () => fetchSpaces(filter),
  })
}
